/** Staged protocol 1 contract. Not registered on the active transport; see docs/e2e.md. */
import { z } from 'zod';
import { Id, TimestampMs } from './common.js';
import { KeysSyncFrame, OpaqueId, Sealed, SealedEvent, SealedRevision, SignedCommandV1 as SignedCommand, } from './e2e.js';
import { StorageStatusFrame } from './history-v1.js';
import { CommandRejectReason, DeviceEndorsement, WebAuthnAssertion } from './signing.js';
import { E2E_PROTOCOL_VERSION as PROTOCOL_VERSION } from './version.js';
/**
 * Wire protocol: vikod ⇄ UserHub ⇄ clients, JSON frames over WSS.
 *
 * Delivery rules (docs/protocol.md):
 *  - The daemon assigns per-run seq; it keeps events in its outbox until the hub acks.
 *  - The hub dedupes on (runId, seq) and acks the highest *contiguous* seq only after
 *    a durable write.
 *  - Clients keep their own per-run cursor and resume with `subscribe{fromSeq}`.
 *  - Every command is a SignedCommand; `envelope.id` is the idempotency key and the daemon
 *    executes each id at most once (a duplicate gets the original outcome).
 */
// ---------------------------------------------------------------------------------------------
// Frames
// ---------------------------------------------------------------------------------------------
const f = (type, shape) => z.strictObject({ type: z.literal(type), ...shape });
export const Role = z.enum(['daemon', 'client']);
export const HelloFrame = f('hello', {
    v: z.literal(PROTOCOL_VERSION),
    role: Role,
    /** Daemon: machine id. Client: device id. */
    peerId: Id,
    softwareVersion: z.string(),
    capabilities: z.array(z.string()).optional(),
});
/** hub → daemon: `createDaemonAuthChallenge()` = { hubNonce (256-bit), issuedAt }. */
export const ChallengeFrame = f('challenge', {
    hubNonce: z.string().min(16),
    issuedAt: TimestampMs,
});
/** daemon → hub: `respondToDaemonAuthChallenge()` output (@viko/crypto DaemonAuthResponse). */
export const AuthFrame = f('auth', {
    statement: z.object({
        v: z.literal(1),
        type: z.literal('viko.daemon-auth'),
        machineId: Id,
        daemonKeyId: Id,
        hubNonce: z.string(),
        hubOrigin: z.string(),
        issuedAt: TimestampMs,
    }),
    /** base64url signature under `viko/daemon-auth/v1`. */
    signature: z.string(),
});
export const WelcomeFrame = f('welcome', {
    v: z.literal(PROTOCOL_VERSION),
    sessionId: Id,
    serverTime: TimestampMs,
    /** Daemon only: highest durably stored seq per run, so the outbox can resend the rest. */
    acked: z.array(z.object({ runId: Id, seq: z.number().int().nonnegative() })).optional(),
});
/**
 * daemon → hub (new events) and hub → client (fan-out / replay). Events are contiguous and
 * ascending. Protocol 1: every event is a `SealedEvent` (plaintext routing metadata + ciphertext).
 */
export const EventBatchFrame = f('event.batch', {
    runId: Id,
    events: z.array(SealedEvent).min(1),
    /** hub → client: true while replaying history below the live edge. */
    replay: z.boolean().optional(),
});
/** hub → daemon: every event with seq ≤ `seq` for `runId` is durably stored. */
export const AckFrame = f('ack', {
    runId: Id,
    seq: z.number().int().nonnegative(),
});
/** client → hub: start streaming `runId` from `fromSeq` (exclusive: send events with seq > fromSeq). */
export const SubscribeFrame = f('subscribe', {
    runId: Id,
    fromSeq: z.number().int().nonnegative(),
    /** Include live-only kinds (asst.delta) when subscribing at the live edge. Default true. */
    live: z.boolean().optional(),
});
export const UnsubscribeFrame = f('unsubscribe', { runId: Id });
/** client → hub: persist the client's read cursor (for unread counts / cross-device resume). */
export const CursorFrame = f('cursor', {
    runId: Id,
    seq: z.number().int().nonnegative(),
});
/** hub → client: history below `toSeq` lives in cold storage chunks (R2); page with range reads. */
export const HistoryFrame = f('history', {
    runId: Id,
    chunks: z.array(z.object({
        key: z.string(),
        firstSeq: z.number().int().positive(),
        lastSeq: z.number().int().positive(),
        bytes: z.number().int().nonnegative(),
    })),
    /** Seq where the hot (DO SQLite) range begins. */
    hotFromSeq: z.number().int().positive(),
});
export const CommandFrame = f('command', { command: SignedCommand });
export const CommandStatus = z.enum([
    'accepted',
    'duplicate',
    'rejected',
    'delivered',
    'done',
    'failed',
]);
/** A short machine-readable error code (never free text: it is stored in plaintext). */
export const ErrorCode = z
    .string()
    .max(64)
    .regex(/^[a-z0-9_.:-]+$/);
/** hub/daemon → client: lifecycle of a command, keyed by `envelope.id`. */
export const CommandResultFrame = f('command.result', {
    commandId: Id,
    status: CommandStatus,
    /** Set when status = rejected. */
    reason: CommandRejectReason.optional(),
    /** Protocol 1: an error code only; error text travels inside `result`. */
    error: ErrorCode.optional(),
    /**
     * Protocol 1: `CommandResultContent` sealed by the daemon at
     * `{ns: machineId, id: commandId, rev: 1, kind: 'command.result'}`.
     */
    result: Sealed.optional(),
    /** Plaintext routing results the hub needs (ids only), e.g. the created runId. */
    route: z.strictObject({ runId: OpaqueId.optional() }).optional(),
});
export const PingFrame = f('ping', { t: TimestampMs });
export const PongFrame = f('pong', { t: TimestampMs });
export const ErrorFrame = f('error', {
    code: z.enum([
        'unsupported_version',
        'auth_failed',
        'bad_frame',
        'seq_gap',
        'not_found',
        'forbidden',
        'internal',
    ]),
    message: z.string(),
    runId: z.string().optional(),
    /** seq_gap: the next seq the hub expects. */
    expectedSeq: z.number().int().positive().optional(),
});
// ---------------------------------------------------------------------------------------------
// Additive (v0): daemon ⇄ hub control frames. Hubs/daemons that predate them ignore them.
// ---------------------------------------------------------------------------------------------
/**
 * daemon → hub request (MCP tool backends that live in the hub: tasks, handoff, history,
 * artifacts, cross-machine release dispatch). The hub answers with `rpc.result{id}`.
 * Methods: see `DAEMON_RPC_METHODS`.
 */
export const RpcFrame = f('rpc', {
    id: Id,
    method: z.string(),
    params: z.unknown().optional(),
});
export const RpcResultFrame = f('rpc.result', {
    id: Id,
    ok: z.boolean(),
    result: z.unknown().optional(),
    error: z.object({ code: z.string(), message: z.string() }).optional(),
});
/** Hub RPC methods a daemon may call (params/results are documented in docs/protocol.md). */
export const DAEMON_RPC_METHODS = [
    'task.get',
    'task.update',
    'task.create',
    'workstream.get',
    'handoff',
    'history.search',
    'artifact.register',
    'artifact.get',
    'release.dispatch',
    'history.import',
];
const SyncTask = z.object({
    id: Id,
    title: z.string(),
    status: z.string(),
    workstreamId: z.string().optional(),
    labels: z.array(z.string()).optional(),
    dependsOn: z.array(z.string()).optional(),
    acceptance: z.string().optional(),
});
/**
 * What a daemon needs to know about a project to run, land and ship it on *this* machine
 * (a projection of `viko.project.yaml` + hub state). Cached by the daemon.
 */
export const ProjectSnapshot = z.object({
    id: Id,
    name: z.string(),
    /** Main clone on this machine. */
    repoPath: z.string().optional(),
    remote: z.string().optional(),
    defaultBranch: z.string().optional(),
    worktree: z
        .object({
        strategy: z.enum(['worktree', 'pool', 'single-lane']),
        root: z.string().optional(),
        poolSize: z.number().int().positive().optional(),
    })
        .optional(),
    landing: z
        .object({
        targetBranch: z.string().optional(),
        mergeMode: z.enum(['ff', 'merge', 'squash', 'pr']).optional(),
        /** Shell command lines, run in order in the worktree after every rebase. */
        test: z.array(z.string()).optional(),
        build: z.array(z.string()).optional(),
    })
        .optional(),
    loopProfile: z.enum(['fast', 'balanced', 'careful']).optional(),
    autonomy: z
        .object({
        level: z.string().optional(),
        overrides: z.record(z.string(), z.enum(['auto', 'ask'])).optional(),
    })
        .optional(),
    /** `viko.project.yaml` release lanes, verbatim. */
    releaseLanes: z.array(z.record(z.string(), z.unknown())).optional(),
    /** `viko.project.yaml` secrets section, verbatim. */
    secrets: z.record(z.string(), z.unknown()).optional(),
    workstreams: z
        .array(z.object({
        id: Id,
        name: z.string(),
        branch: z.string().optional(),
        brief: z.string().optional(),
    }))
        .optional(),
    tasks: z.array(SyncTask).optional(),
    rules: z.array(z.object({ id: Id, text: z.string(), scope: z.string() })).optional(),
    /** Additive: explicit consent for history import/adoption; both default off. */
    history: z.object({ import: z.boolean().optional(), adopt: z.boolean().optional() }).optional(),
});
/**
 * Protocol 1: what the hub sends a daemon about a project — metadata plus sealed revisions. The
 * daemon decrypts them and builds its `ProjectSnapshot` locally (repo path for this machine etc.).
 */
export const SealedProjectBundle = z.strictObject({
    id: OpaqueId,
    status: z.string().max(32).optional(),
    content: SealedRevision,
    workstreams: z.array(z.strictObject({
        id: OpaqueId,
        status: z.string().max(32).optional(),
        machineId: OpaqueId.optional(),
        content: SealedRevision,
    })),
    tasks: z.array(z.strictObject({
        id: OpaqueId,
        status: z.string().max(32),
        workstreamId: OpaqueId.optional(),
        labels: z.array(z.string().max(32)).optional(),
        dependsOn: z.array(OpaqueId).optional(),
        content: SealedRevision,
    })),
    rules: z.array(z.strictObject({ id: OpaqueId, scope: z.string().max(32), content: SealedRevision })),
});
/** hub → daemon: project bundles for this machine (`full` = replace the whole cache). */
export const ProjectSyncFrame = f('project.sync', {
    projects: z.array(SealedProjectBundle),
    full: z.boolean().optional(),
});
export const LandingState = z.enum(['awaiting_approval', 'queued', 'running', 'landed', 'failed']);
/**
 * daemon → hub: landing pipeline progress (one per step transition) and terminal result.
 * Protocol 1: branch/summary/step/detail/result are `LandingContent` sealed at
 * `{ns:'landing', id: landingId, rev, kind:'landing'}` (rev increments per update).
 */
export const LandingUpdateFrame = f('landing.update', {
    landingId: OpaqueId,
    state: LandingState,
    projectId: OpaqueId.optional(),
    workstreamId: OpaqueId.optional(),
    runId: OpaqueId.optional(),
    taskIds: z.array(OpaqueId).optional(),
    stepStatus: z.enum(['start', 'ok', 'fail', 'skip']).optional(),
    attempt: z.number().int().positive().optional(),
    commitSha: z
        .string()
        .regex(/^[0-9a-f]{7,64}$/)
        .optional(),
    /** Terminal status of the pipeline (`LandingResult.status`). */
    outcome: z.enum(['landed', 'failed', 'conflict', 'rejected', 'aborted']).optional(),
    content: SealedRevision,
    ts: TimestampMs,
});
/** daemon → hub: release-lane build card updates. Protocol 1: the BuildRecord is sealed. */
export const BuildUpdateFrame = f('build.update', {
    buildId: OpaqueId,
    projectId: OpaqueId,
    laneId: OpaqueId,
    status: z.string().max(32),
    taskIds: z.array(OpaqueId).optional(),
    workstreamIds: z.array(OpaqueId).optional(),
    content: SealedRevision,
});
/**
 * daemon → hub: outcome of a `brain.triage` / `brain.retro` command (same `requestId`).
 * Protocol 1: the outcome (`TriageOutcome` result + card, or retro `{set, llm?, llmError?}`, plus
 * error text) is sealed at `{ns:'brain.result', id: requestId, rev: 1, kind:'brain.result'}`.
 */
export const BrainResultFrame = f('brain.result', {
    requestId: OpaqueId,
    kind: z.enum(['triage', 'retro']),
    ok: z.boolean(),
    /** Counts only (for the card title): proposed workstreams / tasks / notes. */
    counts: z
        .strictObject({
        workstreams: z.number().int().nonnegative(),
        tasks: z.number().int().nonnegative(),
        notes: z.number().int().nonnegative(),
    })
        .optional(),
    sealed: Sealed.optional(),
    error: ErrorCode.optional(),
    /** Numbers only: attempts, costUsd, durationMs. */
    meta: z
        .strictObject({
        attempts: z.number().int().nonnegative().optional(),
        costUsd: z.number().nonnegative().optional(),
        durationMs: z.number().nonnegative().optional(),
    })
        .optional(),
});
/**
 * daemon → hub: machine readiness + load (after welcome, on change, periodically).
 * Protocol 1: doctor fix cards and blocker texts are `MachineStatusContent` sealed at
 * `{ns:'machine', id: machineId, rev, kind:'machine'}`; only counts, capabilities and ids stay plain.
 */
export const MachineStatusFrame = f('machine.status', {
    ts: TimestampMs,
    activeRuns: z.number().int().nonnegative(),
    maxRuns: z.number().int().positive(),
    doctor: z
        .strictObject({
        at: z.string().max(40),
        summary: z.record(z.string().max(32), z.number()),
        capabilities: z.array(z
            .string()
            .max(64)
            .regex(/^[A-Za-z0-9:._@+-]+$/)),
        /** Fix card ids (check ids) whose text is sealed. */
        fixIds: z.array(z.string().max(128)).optional(),
    })
        .optional(),
    /** Open "Blocked: environment" records (text sealed). */
    blockers: z
        .array(z.strictObject({
        id: OpaqueId,
        runId: OpaqueId,
        cause: z
            .string()
            .max(64)
            .regex(/^[a-z0-9_.-]+$/),
        autoFix: z.boolean(),
        detectedAt: TimestampMs,
    }))
        .optional(),
    content: SealedRevision.optional(),
    /** Pending release-lane approvals (tap / passkey to ship). */
    pendingShips: z
        .array(z.object({
        projectId: z.string(),
        laneId: z.string(),
        gate: z.string(),
        landings: z.number().int().nonnegative(),
    }))
        .optional(),
});
// ---------------------------------------------------------------------------------------------
// Additive (v0): control-plane frames (first defined in apps/control, moved here verbatim).
// ---------------------------------------------------------------------------------------------
export const InboxItem = z.object({
    /** questionId / approvalId. Unique only together with runId. */
    id: z.string(),
    runId: z.string(),
    /** Additive: `land` (landing awaiting a tap), `build` (ship tap), `card` (decision card). */
    type: z.enum(['question', 'approval', 'land', 'build', 'card']),
    seq: z.number().int(),
    /** Protocol 1: absent (titles are content; clients decrypt the item's event or entity). */
    title: z.string().optional(),
    blocking: z.boolean(),
    createdAt: TimestampMs,
    score: z.number(),
});
export const RunSummary = z.object({
    runId: z.string(),
    machineId: z.string(),
    agent: z.string().optional(),
    model: z.string().optional(),
    status: z.string(),
    headSeq: z.number().int(),
});
/** hub → client: open questions/approvals, ranked (full snapshot on every change). */
export const InboxFrame = f('inbox', { items: z.array(InboxItem) });
/** hub → client: run metadata changed (status, model, head seq). */
export const RunFrame = f('run', { run: RunSummary });
/** `@viko/crypto` RevocationList (passkey-signed). */
export const RevocationList = z.object({
    v: z.literal(1),
    type: z.literal('viko.revocations'),
    seq: z.number().int().nonnegative(),
    issuedAt: TimestampMs,
    devices: z.array(z.string()),
    credentials: z.array(z.string()),
});
/**
 * hub → daemon, after `welcome` and on every change: endorsements and the latest signed
 * revocation list for the daemon's TrustStore. The daemon verifies each item itself
 * (docs/signing.md §4, §8); the hub only relays.
 */
export const TrustSyncFrame = f('trust.sync', {
    endorsements: z.array(DeviceEndorsement),
    revocations: z.object({ list: RevocationList, assertion: WebAuthnAssertion }).nullable(),
    /**
     * Additive: "Just this session" devices whose sign-in the hub ended (sign-out, pagehide, removal
     * from Devices) and whose endorsement has not expired yet. Daemons refuse them at once; this
     * never affects a trusted device (TrustStore.endSessionDevices).
     */
    endedSessions: z.array(z.string().min(1).max(256)).max(1024).optional(),
});
/**
 * daemon → hub: request a presigned R2 upload URL on the artifact origin. Protocol 1: the object is
 * an encrypted artifact blob (`@viko/e2e` artifact codec); name/MIME are sealed metadata.
 */
export const ArtifactUploadFrame = f('artifact.upload', {
    reqId: z.string(),
    artifactId: OpaqueId,
    runId: OpaqueId.optional(),
    /** Ciphertext length. */
    bytes: z.number().int().nonnegative().optional(),
});
/** hub → daemon: presigned PUT URL (15 min). */
export const ArtifactUrlFrame = f('artifact.url', {
    reqId: z.string(),
    artifactId: z.string(),
    key: z.string(),
    url: z.string(),
    expiresAt: TimestampMs,
    /** Additive: artifact storage (R2) is not configured; keep the file queued (`storage.status`). */
    unavailable: z.boolean().optional(),
});
/** daemon → hub: per-repo landing lock (docs/PLAN.md landing pipeline). */
export const LockAcquireFrame = f('lock.acquire', {
    reqId: z.string(),
    repo: z.string(),
    ttlMs: z.number().int().positive().optional(),
});
export const LockReleaseFrame = f('lock.release', {
    reqId: z.string(),
    repo: z.string(),
    token: z.string(),
});
export const LockResultFrame = f('lock.result', {
    reqId: z.string(),
    ok: z.boolean(),
    token: z.string().optional(),
    expiresAt: TimestampMs.optional(),
    holder: z.string().optional(),
});
export const Frame = z.discriminatedUnion('type', [
    KeysSyncFrame,
    HelloFrame,
    ChallengeFrame,
    AuthFrame,
    WelcomeFrame,
    EventBatchFrame,
    AckFrame,
    SubscribeFrame,
    UnsubscribeFrame,
    CursorFrame,
    HistoryFrame,
    CommandFrame,
    CommandResultFrame,
    PingFrame,
    PongFrame,
    ErrorFrame,
    RpcFrame,
    RpcResultFrame,
    ProjectSyncFrame,
    LandingUpdateFrame,
    BuildUpdateFrame,
    MachineStatusFrame,
    BrainResultFrame,
    InboxFrame,
    RunFrame,
    TrustSyncFrame,
    ArtifactUploadFrame,
    ArtifactUrlFrame,
    LockAcquireFrame,
    LockReleaseFrame,
    LockResultFrame,
    StorageStatusFrame,
]);
/** R2 cold-storage chunking: a chunk closes at whichever limit is hit first. */
export const R2_CHUNK_LIMITS = {
    maxEvents: 512,
    maxBytes: 1024 * 1024,
    maxAgeMs: 60_000,
};
//# sourceMappingURL=wire-v1.js.map